import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ItemCardAuctionName } from '@/components/GridItemCard/Card/components/ItemCardAuctionName/ItemCardAuctionName';
import { ItemCardCountdown } from '@/components/GridItemCard/Card/components/ItemCardCountdown/ItemCardCountdown';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import { ImageRow } from '@/components/ItemCard/ImageRow';
import { useIsBrowsingPage } from '@/hooks/useIsBrowsingPage';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useAppSelector } from '@/redux/hooks';
import {
    getIsItemSummaryTimedPlusBiddingClosed,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsReserveMet,
    getItemSummaryIsSold,
    getItemSummaryStartTime,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger, useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics';
import { LotRecommendationTrigger } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { ItemCardComponent, ItemCardInfo } from '../../../types/types';
import { RegisterLinkOrPrice } from './components/RegisterLinkOrPrice/RegisterLinkOrPrice';
import TimedBadgeContainer from './components/TimedBadgeContainer/TimedBadgeContainer';

const TimedPlusItemCard: ItemCardComponent = ({
    className,
    dimension,
    hideCardMenu,
    itemId,
    recommendationPosition,
    recommendationType,
}) => {
    const formatMessage = useAppFormatMessage();
    const { pathname } = useLocation();
    const isCatalogPage = pathname.includes('/catalog');

    const [linkHover, setLinkHover] = useState(false);

    const saleStartTs = useAppSelector((state) => getItemSummaryStartTime(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const isTimedPlusBiddingClosed = useAppSelector((state) => getIsItemSummaryTimedPlusBiddingClosed(state, itemId));
    const isReserveMet = useAppSelector((state) => getItemSummaryIsReserveMet(state, itemId));
    const { onNavigateInitiated } = useItemCardNavigation(itemId);
    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const showStatusBanner = !pathname.includes('/c/') && (!isSold || isReserveMet) && !isPaused;

    const isMobile = useIsMobileLayout();
    const displayItemName = isMobile && (isSold || isPassed || isPaused || saleStartTs > Math.round(Date.now() / 1000));

    const isBrowsingPage = useIsBrowsingPage();

    const enterLinkHoverState = useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHover(false);
    }, []);

    const onRecommendationClick = useRecommendationTracking({
        itemId,
        recommendationPosition,
        recommendationType,
    });

    return (
        <article
            className={className}
            data-testid={`timed-plus-item-card-${itemId}`}
            style={{ width: dimension ? `${dimension}px` : 'auto' }}
        >
            <section
                className="relative grid w-[inherit] min-w-0 justify-center [grid-template-areas:'img'_'.'_'time'_'.'_'title'_'.'_'registerlinkorprice'_'.'_'easyshipping'_'.'] [grid-template-rows:min-content_22px_min-content_4px_min-content_4px_min-content_24px]"
                style={{ gridTemplateColumns: dimension ? `${dimension}px` : '1fr' }}
            >
                <div className="absolute right-xs top-xs z-[2]">
                    <ItemCardSaveIcon
                        itemId={itemId}
                        labeled={!isMobile && isBrowsingPage}
                    />
                </div>
                <TimedBadgeContainer itemId={itemId} />
                <div
                    className="relative aspect-square [grid-area:img]"
                    onMouseEnter={enterLinkHoverState}
                    onMouseLeave={leaveLinkHoverState}
                >
                    <ImageRow
                        itemId={itemId}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.ImageClick);
                            onRecommendationClick(LotRecommendationTrigger.ImageClick);
                        }}
                        showBidStatus={showStatusBanner}
                        showImageWithHover={!isMobile}
                    />
                </div>

                <div
                    className="flex min-w-0 justify-between overflow-hidden text-ellipsis whitespace-nowrap [grid-area:time]"
                    data-testid="timed-plus-item-card.catalog-information"
                >
                    {isTimedPlusBiddingClosed ? (
                        <BodySecondary>{formatMessage(['lot_closed'])}</BodySecondary>
                    ) : (
                        <div
                            className="flex w-full justify-between"
                            data-testid="timed-plus-item-card.catalog-information.date-information"
                        >
                            <ItemCardCountdown
                                itemId={itemId}
                                onNavigate={() => onNavigateInitiated(ItemCardNavigationTrigger.CountdownClick)}
                            />
                            {!isCatalogPage && !isMobile && (
                                <ItemCardAuctionName
                                    itemId={itemId}
                                    onNavigate={() => {
                                        onNavigateInitiated(ItemCardNavigationTrigger.HouseNameClick);
                                        onRecommendationClick(LotRecommendationTrigger.HouseNameClick);
                                    }}
                                />
                            )}
                            {!isCatalogPage && isMobile && displayItemName && (
                                <ItemCardAuctionName
                                    itemId={itemId}
                                    onNavigate={() => {
                                        onNavigateInitiated(ItemCardNavigationTrigger.HouseNameClick);
                                        onRecommendationClick(LotRecommendationTrigger.HouseNameClick);
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className="[grid-area:title]">
                    <ItemCardTitle
                        isHovered={linkHover}
                        itemId={itemId}
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.TitleClick);
                            onRecommendationClick(LotRecommendationTrigger.TitleClick);
                        }}
                        showLotNumber
                    />
                </div>
                <RegisterLinkOrPrice
                    hideCardMenu={hideCardMenu}
                    itemId={itemId}
                    linkHover={linkHover}
                    onNavigate={() => {
                        onNavigateInitiated(ItemCardNavigationTrigger.PriceClick);
                    }}
                    onRegisterForAuctionClicked={() => trackRegisterForAuctionPressedAnalytics()}
                />
            </section>
        </article>
    );
};

const info: ItemCardInfo = {
    dimensions: { desktop: 184, mobile: 164 },
    ItemCardComponent: TimedPlusItemCard,
};

export default info;
